import type { PortalTransactionListItemDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';

/** Status pill colors — mirrors the CRM transaction status mapping. */
function statusClasses(status: string | null): string {
	switch ((status ?? '').toLowerCase()) {
		case 'succeeded':
		case 'completed':
		case 'settled':
			return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
		case 'failed':
		case 'error':
			return 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300';
		case 'processing':
		case 'pending':
			return 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300';
		default:
			// Voided / Refunded / unknown — neutral terminal states
			return 'bg-gray-100 dark:bg-gray-700/40 text-gray-700 dark:text-gray-300';
	}
}

/** "Visa ****4242" / "Account Funds" / "Check" — whatever describes it best. */
function methodLabel(txn: PortalTransactionListItemDto): string {
	const subtype = txn.mPaymentPaymentMethodSubtype ?? '';
	const lastFour = txn.mPaymentPaymentMethodLastFour ?? '';
	if (subtype !== '' && lastFour !== '') {
		return `${subtype} ****${lastFour}`;
	}
	return txn.mPaymentPaymentMethodType;
}

/**
 * One transaction row (PD-14080) — shared by the account page's Recent
 * Transactions card and the full Transactions page so the two always agree.
 */
export function TransactionRow({
	txn,
	showClient,
}: {
	txn: PortalTransactionListItemDto;
	showClient: boolean;
}): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<div className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3">
			<div className="min-w-0">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="text-sm font-medium text-gray-900 dark:text-gray-100">
						{t(txn.mPaymentType ?? 'Transaction')}
					</span>
					<span
						className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClasses(txn.mPaymentStatus)}`}
					>
						{t(txn.mPaymentStatus ?? '-')}
					</span>
				</div>
				{/* One meta line — related records sit inline next to the
				    method so the card matches the height of the order /
				    invoice / statement tiles (PD-14080 review feedback). */}
				<div className="text-xs text-brand-muted mt-0.5">
					{formatStatementDate(txn.mPaymentDatetime as unknown as string)}
					{' · '}
					{t(methodLabel(txn))}
					{showClient && txn.clientName !== '' && (
						<>
							{' · '}
							{t(txn.clientName)}
						</>
					)}
					{txn.relatedInvoiceIds.map((invoiceId, index) => (
						<span key={invoiceId}>
							{index === 0 ? ' · ' : ', '}
							<button
								type="button"
								onClick={() => {
									void navigate(`/invoices/${invoiceId}`);
								}}
								className="text-portal-accent hover:underline"
							>
								{index === 0 ? `${t('Invoice')} ` : ''}#{invoiceId}
							</button>
						</span>
					))}
					{/* PD-13704: a successful payment with no invoice links is money
					    put on the customer's account — describe it instead of leaving
					    the row as a bare "Capture". */}
					{txn.relatedInvoiceIds.length === 0 &&
						/capture|sale|payment/i.test(txn.mPaymentType ?? '') &&
						/succe/i.test(txn.mPaymentStatus ?? '') && (
							<span>
								{' · '}
								{t('Payment on account')}
							</span>
						)}
				</div>
			</div>
			<div className="text-right shrink-0">
				<div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
					{formatCurrency(txn.mPaymentAmount)}
				</div>
			</div>
		</div>
	);
}
